import {
  Autocomplete,
  Box,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { frFR } from "@mui/x-data-grid/locales";
import { RefreshOutlined } from "@mui/icons-material";
import { AddOutlined, EditOutlined } from "@material-ui/icons";
import { useQuery } from "../../../../../../hooks/useQuery";
import useToast from "../../../../../../hooks/useToast";
import { DomaineService } from "../../../../../../service/DomaineService";
import { TYPE_NOTIFICATION } from "../../../../shares/NotificationBull";
import AppButton from "../../../../shares/AppButton";
import DomaineForm from "./DomaineForm";
import { DomaineParAMIService } from "../../../../../../service/DomaineParAMIService";
import { AMIService } from "../../../../../../service/AMI/AMIService";

const AssignerDomaineList = ({ open, onFormClose }) => {
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [amiList, setAmiList] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [initialSelectedIds, setInitialSelectedIds] = useState([]);
  const [idSelectedQuestion, setIdSelectedQuestion] = useState(null);
  const [idSelectedDomaine, setidSelectedDomaine] = useState(null);
  const [filtre, setFiltre] = useState({
    mot_cle: "",
    id_ami : null
  });
  const urlQuery = useQuery();
  const id_type = urlQuery.get("ref");
  const { Toast, showToast } = useToast();

  const handleFormClose = () => {
    onFormClose();
    setSelectedIds([]);
    handleFetchData();
    setFiltre({ mot_cle: "", id_ami : null });
  };

  const handleCloseDomaineForm = () => {
    setOpenFormDialog(false);
    setIdSelectedQuestion(null);
    handleFetchData();
  };

  const handleFilter = useCallback(
    (filtre) => {
      if (!filtre || String(filtre).trim() === "") {
        setFilteredRows(rows);
        return;
      }
      const mots = filtre
        .toLowerCase()
        .split(" ")
        .filter((mot) => mot.trim() !== "");
      setFilteredRows(
        rows.filter((item) =>
          mots.every((mot) =>
            Object.values(item).some(
              (val) => val && val.toString().toLowerCase().includes(mot),
            ),
          ),
        ),
      );
    },
    [rows],
  );

  const handleOpenCreationQuestionDialog = (idForm) => {
    setOpenFormDialog(true);
    setidSelectedDomaine(idForm);
  };

  const handleFetchAmiList = () => {
    AMIService.list({}).then((res) => {
      if (res && res.data) {
        setAmiList(res.data);
      }
    });
  };

  const handleFetchData = () => {
    setFormLoading(true);
    DomaineService.lister({ id_type: id_type, id_ami: filtre?.id_ami })
      .then((res) => {
        if (res && res.data) {
          setRows(res.data);
          const initiallySelected = res.data
            .filter((r) => r.id_ext_form_question != null)
            .map((r) => r.id_sqc_question);
          setSelectedIds(initiallySelected);
          setInitialSelectedIds(initiallySelected);
        }
      })
      .finally(() => {
        setFormLoading(false);
      });
  };

  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id],
    );
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id_sqc_question === id ? { ...row, selected: !row.selected } : row,
      ),
    );
  };

  const handleSelectDataGridRow = (value) => {
    setSelectedIds(value);
  };

  const getInsertableRows = () => {
    return {
      id_type: id_type,
      id_domaines: selectedIds,
    };
  };

  const handleSave = () => {
    setLoading(true);
    DomaineParAMIService.insert(getInsertableRows())
      .then((res) => {
        if (res.success) {
          showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
          handleFormClose();
        } else {
          showToast(TYPE_NOTIFICATION.ERROR, res.message);
        }
      })
      .finally(() => {
        setLoading(false);
      });
    setInitialSelectedIds(selectedIds); // Met à jour l'état initial
  };

  const table_colonne = [
    {
      field: "name",
      headerName: "Domaine",
      flex: 1,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
    },
    {
      field: "",
      headerName: "Actions",
      flex: 0.3,
      renderCell: (params) => {
        return (
          <Tooltip title="Modifier la domaine">
            <IconButton
              color="secondary"
              onClick={(e) =>
                handleOpenCreationQuestionDialog(params.row.id_domaine)
              }
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  useEffect(() => {
    handleFilter(filtre.mot_cle);
  }, [filtre, handleFilter, rows]);

  useEffect(() => {
    handleFetchData();
  }, [filtre.id_ami]);

  useEffect(() => {
    if (open) {
      handleFetchData();
      handleFetchAmiList();
    }
  }, [open]);

  return (
    <>
      <DomaineForm
        open={openFormDialog}
        onFormClose={handleCloseDomaineForm}
        id_domaine={idSelectedDomaine}
      />
      <Dialog open={open} onClose={handleFormClose} fullWidth maxWidth="lg">
        <Toast />
        <Paper sx={{ m: 3 }} elevation={0}>
          <Grid>
            <CardHeader
              title={"Assigner domaine"}
              action={
                <Grid container spacing={1} justifyContent="space-around">
                  <Grid item>
                    <Autocomplete
                      sx={{ width: 250 }}
                      options={amiList}
                      getOptionLabel={(option) => option.libelle_type}
                      onChange={(e, value) => {
                        setFiltre((prev) => {
                          return { ...prev, id_ami: value?.id_type || "" };
                        });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} size="small" placeholder="Filtrer par AMI lié" />
                      )}
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      id="recherche"
                      fullWidth
                      size="small"
                      onChange={(e) =>
                        setFiltre((prev) => {
                          return { ...prev, mot_cle: e.target.value };
                        })
                      }
                      placeholder="Recherche"
                      InputProps={{
                        endAdornment: (
                          <IconButton>
                            <SearchIcon />
                          </IconButton>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <IconButton
                      title="Actualiser"
                      color="primary"
                      onClick={handleFetchData}
                    >
                      <RefreshOutlined />
                    </IconButton>
                  </Grid>
                  <Grid item>
                    <IconButton
                      onClick={(e) => handleOpenCreationQuestionDialog(null)}
                      color="primary"
                      title="Creer une domaine"
                    >
                      <AddOutlined />
                    </IconButton>
                  </Grid>
                </Grid>
              }
            />
            <CardContent>
              <Grid container /* display={formLoading ? "none" : ""} */>
                <Box display={formLoading ? "" : "none"} sx={{ width: "100%" }}>
                  <LinearProgress />
                </Box>
                <DataGrid
                  sx={{
                    width: {
                      xs: "75.5vw",
                      sm: "90vw",
                      md: "93.5vw",
                      lg: "79.5vw",
                      height: "60vh",
                    },
                  }}
                  columns={table_colonne}
                  rows={filteredRows}
                  getRowId={(row) => row.id_domaine}
                  localeText={
                    frFR.components.MuiDataGrid.defaultProps.localeText
                  }
                  checkboxSelection
                  disableRowSelectionOnClick
                  onRowSelectionModelChange={(newSelection) => {
                    handleSelectDataGridRow(newSelection);
                  }}
                />
              </Grid>
            </CardContent>
            <CardActions sx={{ justifyContent: "flex-end" }}>
              <AppButton
                loading={loading}
                text={"Enregistrer"}
                variant={"contained"}
                size={"medium"}
                onClick={handleSave}
              />
              <AppButton
                text={"Fermer"}
                size={"medium"}
                variant={"outlined"}
                onClick={handleFormClose}
              />
            </CardActions>
          </Grid>
        </Paper>
      </Dialog>
    </>
  );
};

export default AssignerDomaineList;
